import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { Loader, LoaderStages } from "@/components/Loader";
import { ArrowRight, Sparkles, GitCompareArrows, TrendingUp, AlertTriangle, Lightbulb, Link2, Unlink, Wand2, Pin, Download, Image as ImageIcon, Trophy, Plus, Minus, Mail, Copy, X, FileText, RefreshCw } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { synergyToMarkdown, downloadBlob } from "@/lib/export";
import ShareAnalytics from "@/components/ShareAnalytics";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${Number(n).toFixed(0)}`;
};

// Extract the opaque token out of a /p/<token> share URL
const extractToken = (url) => {
    if (!url) return "";
    const m = url.match(/\/p\/([a-f0-9]{8,})/i);
    return m ? m[1] : "";
};

function FirmSelect({ label, value, onChange, firms, excludeId, testid }) {
    return (
        <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">{label}</label>
            <select
                data-testid={testid}
                value={value || ""}
                onChange={(e) => onChange(e.target.value || null)}
                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500 font-mono"
            >
                <option value="">— pick a firm —</option>
                {firms.filter((f) => f.id !== excludeId).map((f) => (
                    <option key={f.id} value={f.id}>{`${f.name}${f.industry ? ` · ${f.industry}` : ""}`}</option>
                ))}
            </select>
        </div>
    );
}

const FitGauge = ({ score }) => {
    const pct = Math.max(0, Math.min(100, score || 0));
    const tone = pct >= 75 ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : pct >= 50 ? "text-amber-400 border-amber-500/40 bg-amber-500/10" : "text-red-400 border-red-500/40 bg-red-500/10";
    return (
        <div className={`border ${tone} px-4 py-3 inline-flex items-center gap-3`} data-testid="fit-score">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-70">Fit score</div>
            <div className="font-heading font-black text-3xl leading-none">{pct}<span className="text-base opacity-50">/100</span></div>
        </div>
    );
};

export default function Synergy() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [firms, setFirms] = useState(null);
    const [firmAId, setFirmAId] = useState(null);
    const [productId, setProductId] = useState(null);
    const [firmBId, setFirmBId] = useState(null);
    const [autoBest, setAutoBest] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [pinned, setPinned] = useState(false);
    const [pinning, setPinning] = useState(false);
    const [productImage, setProductImage] = useState("");
    const [genImg, setGenImg] = useState(false);
    const [imgError, setImgError] = useState("");
    const [reopenInfo, setReopenInfo] = useState(""); // banner shown when a saved synergy has no stored analysis
    const [email, setEmail] = useState(null); // {subject, body, tone}
    const [emailTone, setEmailTone] = useState("casual");
    const [draftingEmail, setDraftingEmail] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [emailCopied, setEmailCopied] = useState(false);
    // C — Multi-touch sequence
    const [sequence, setSequence] = useState(null); // {emails: [...], tone}
    const [draftingSeq, setDraftingSeq] = useState(false);
    const [seqError, setSeqError] = useState("");
    const [seqCopiedIdx, setSeqCopiedIdx] = useState(-1);
    // A — Shareable public pitch link
    const [shareUrl, setShareUrl] = useState("");
    const [sharing, setSharing] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);
    const [shareError, setShareError] = useState("");
    // Per-pitch reply tracking — manual toggle until inbound forwarding is wired.
    const [replied, setReplied] = useState(false);
    const [replyEmail, setReplyEmail] = useState("");
    const [replyToggling, setReplyToggling] = useState(false);
    const [replyAliasCopied, setReplyAliasCopied] = useState(false);
    // Stale-pitch nudge — `last_nudge_at` ISO string when a Claude draft was last emailed
    // to the operator; `nudging` is the in-flight indicator for the manual "Draft a nudge" button.
    const [lastNudgeAt, setLastNudgeAt] = useState("");
    const [nudging, setNudging] = useState(false);
    const [nudgeMsg, setNudgeMsg] = useState("");

    useEffect(() => { api.get("/firms").then(({ data }) => setFirms(data)); }, []);

    // Map of firm.id → color so we can render colored dots next to firm/product mentions
    // wherever a synergy result references them (verdict header, tables, projection).
    const firmColors = useMemo(() => {
        const map = {};
        (firms || []).forEach((f) => { if (f.color) map[f.id] = f.color; });
        return map;
    }, [firms]);

    // Reopen a saved synergy when ?key=firmA:firmB:product is in the URL.
    useEffect(() => {
        const key = searchParams.get("key");
        if (!key) return;
        let cancelled = false;
        (async () => {
            setLoading(true); setError(""); setResult(null); setPinned(false); setReopenInfo("");
            try {
                const { data } = await api.get(`/synergies/${encodeURIComponent(key)}`);
                if (cancelled) return;
                setPinned(Boolean(data.pinned));
                if (data.product_image_b64) setProductImage(data.product_image_b64);
                if (data.share_token) {
                    setShareUrl(`${window.location.origin}/p/${data.share_token}`);
                }
                if (data.reply_email) setReplyEmail(data.reply_email);
                setReplied(Boolean(data.replied));
                if (data.last_nudge_at) setLastNudgeAt(data.last_nudge_at);
                if (data.analysis && data.analysis.fit_score !== undefined) {
                    setResult(data.analysis);
                } else {
                    // Older record — only metadata is saved. Prefill the selectors so the user can re-run.
                    const [fa, fb, pid] = key.split(":");
                    setFirmAId(fa); setFirmBId(fb); setProductId(pid);
                    setReopenInfo("This synergy was saved before full analysis was persisted. Click Analyze fit to re-run.");
                }
            } catch (e) {
                const d = e?.response?.data?.detail;
                setError(typeof d === "string" ? d : "Could not load saved synergy");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Pre-select firm A / firm B when deep-linked from the matrix (?a=X&b=Y).
    // Only runs once on mount and only when `?key=...` is NOT present (key takes precedence).
    useEffect(() => {
        if (searchParams.get("key")) return;
        const a = searchParams.get("a");
        const b = searchParams.get("b");
        if (a) setFirmAId(a);
        if (b) setFirmBId(b);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const firmA = useMemo(() => (firms || []).find((f) => f.id === firmAId), [firms, firmAId]);
    const products = firmA?.products || [];

    // reset product when firmA changes
    useEffect(() => {
        if (firmA && products.length && !products.find((p) => p.id === productId)) {
            setProductId(products[0].id);
        }
        if (!firmA) setProductId(null);
        // eslint-disable-next-line
    }, [firmAId]);

    const run = async () => {
        if (!firmAId || !firmBId) return;
        if (!autoBest && !productId) return;
        // Re-running clears any reopened-from-URL state so the new result isn't overwritten on next render.
        if (searchParams.get("key")) {
            setSearchParams({}, { replace: true });
        }
        setReopenInfo("");
        setLoading(true); setError(""); setResult(null); setPinned(false); setProductImage(""); setImgError("");
        try {
            const { data: enqueued } = autoBest
                ? await api.post("/synergy/best", { firm_a_id: firmAId, firm_b_id: firmBId })
                : await api.post("/synergy", { firm_a_id: firmAId, product_id: productId, firm_b_id: firmBId });
            const result = await pollJob(enqueued.job_id);
            setResult(result);
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            if (rl) setError(rl);
            else if (e.jobError) setError(e.message);
            else setError(typeof d === "string" ? d : "Analysis failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const generateProductImage = async () => {
        if (!result) return;
        setGenImg(true); setImgError("");
        try {
            const { data } = await api.post("/synergies/image", {
                firm_a_id: result.firm_a.id,
                firm_b_id: result.firm_b.id,
                product_id: result.product.id,
            });
            setProductImage(data.product_image_b64 || "");
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setImgError(rl || (typeof d === "string" ? d : "Image generation failed"));
        } finally {
            setGenImg(false);
        }
    };

    const pinSynergy = async () => {
        if (!result) return;
        setPinning(true);
        try {
            await api.post("/synergies/pin", {
                firm_a_id: result.firm_a.id,
                firm_b_id: result.firm_b.id,
                product_id: result.product.id,
                analysis: result, // persist the full dossier so the synergy can be reopened later
            });
            setPinned(true);
        } finally {
            setPinning(false);
        }
    };

    const draftEmail = async (tone = emailTone) => {
        if (!result) return;
        setDraftingEmail(true); setEmailError(""); setEmail(null); setEmailCopied(false);
        try {
            const { data } = await api.post("/synergies/email", {
                firm_a_id: result.firm_a.id,
                firm_b_id: result.firm_b.id,
                product_id: result.product.id,
                tone,
            });
            setEmail({ ...data, tone });
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setEmailError(rl || (typeof d === "string" ? d : "Email draft failed"));
        } finally {
            setDraftingEmail(false);
        }
    };

    const copyEmailToClipboard = async () => {
        if (!email) return;
        try {
            await navigator.clipboard.writeText(`Subject: ${email.subject}\n\n${email.body}`);
            setEmailCopied(true);
            setTimeout(() => setEmailCopied(false), 2000);
        } catch { /* clipboard blocked */ }
    };

    // C — Generate a 5-email outbound cadence (job-polling). Reuses the saved-synergy selling-points
    // so the messages reference the real angles, not generic boilerplate.
    const draftSequence = async (tone = emailTone) => {
        if (!result) return;
        setDraftingSeq(true); setSeqError(""); setSequence(null); setSeqCopiedIdx(-1);
        try {
            const { data: enq } = await api.post("/synergies/email-sequence", {
                firm_a_id: result.firm_a.id,
                firm_b_id: result.firm_b.id,
                product_id: result.product.id,
                tone,
            });
            const r = await pollJob(enq.job_id, { timeoutMs: 5 * 60 * 1000 });
            setSequence({ ...r, tone });
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setSeqError(rl || (typeof d === "string" ? d : (e.jobError ? e.message : "Email sequence failed")));
        } finally {
            setDraftingSeq(false);
        }
    };

    const copySequenceEmail = async (idx) => {
        const em = sequence?.emails?.[idx];
        if (!em) return;
        try {
            await navigator.clipboard.writeText(`Subject: ${em.subject}\n\n${em.body}`);
            setSeqCopiedIdx(idx);
            setTimeout(() => setSeqCopiedIdx(-1), 2000);
        } catch { /* clipboard blocked */ }
    };

    // A — Generate (or retrieve) a public shareable pitch URL. Token is reused if it already exists.
    const generateShareLink = async () => {
        if (!result) return;
        setSharing(true); setShareError(""); setShareCopied(false);
        try {
            const key = `${result.firm_a.id}:${result.firm_b.id}:${result.product.id}`;
            const { data } = await api.post(`/synergies/${encodeURIComponent(key)}/share`);
            // Build the absolute external URL the user can share
            const url = `${window.location.origin}${data.path}`;
            setShareUrl(url);
            // Derive the trackable reply alias from the freshly-minted share token
            // (response shape: { path: '/p/<token>', ... }).
            const tok = (data.path || "").replace(/^\/p\//, "");
            if (tok) setReplyEmail(`pitch-${tok}@reply.affynix.it`);
            try {
                await navigator.clipboard.writeText(url);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2500);
            } catch { /* clipboard blocked */ }
        } catch (e) {
            const d = e?.response?.data?.detail;
            setShareError(typeof d === "string" ? d : "Could not create share link. Pin the synergy first.");
        } finally {
            setSharing(false);
        }
    };

    const revokeShareLink = async () => {
        if (!result) return;
        try {
            const key = `${result.firm_a.id}:${result.firm_b.id}:${result.product.id}`;
            await api.delete(`/synergies/${encodeURIComponent(key)}/share`);
            setShareUrl("");
            setReplyEmail("");
            setReplied(false);
        } catch { /* ignore */ }
    };

    // Toggle the prospect-replied flag. Until inbound forwarding is wired, this is manual.
    const toggleReplied = async () => {
        if (!result) return;
        setReplyToggling(true);
        try {
            const key = `${result.firm_a.id}:${result.firm_b.id}:${result.product.id}`;
            const path = replied ? "unmark-replied" : "mark-replied";
            await api.post(`/synergies/${encodeURIComponent(key)}/${path}`);
            setReplied(!replied);
        } catch { /* ignore */ }
        finally { setReplyToggling(false); }
    };

    const copyReplyAlias = async () => {
        if (!replyEmail) return;
        try {
            await navigator.clipboard.writeText(replyEmail);
            setReplyAliasCopied(true);
            setTimeout(() => setReplyAliasCopied(false), 2200);
        } catch { /* clipboard blocked */ }
    };

    // Manually fire a Claude-drafted re-engagement nudge — emails the operator (CONTACT_EMAIL)
    // with both casual + direct variants. Bypasses the 48h scanner cooldown so admins can
    // request a fresh draft on demand.
    const draftNudge = async () => {
        if (!result || nudging) return;
        setNudging(true); setNudgeMsg("");
        try {
            const key = `${result.firm_a.id}:${result.firm_b.id}:${result.product.id}`;
            const { data } = await api.post(`/synergies/${encodeURIComponent(key)}/draft-nudge`);
            setLastNudgeAt(new Date().toISOString());
            setNudgeMsg(`Nudge emailed to ${data.sent_to || "your inbox"}.`);
            setTimeout(() => setNudgeMsg(""), 6000);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setNudgeMsg(typeof d === "string" ? d : "Failed to draft nudge");
        } finally {
            setNudging(false);
        }
    };

    const downloadMd = () => {
        if (!result) return;
        const name = `synergy-${result.firm_a.name}-${result.firm_b.name}-${new Date().toISOString().slice(0,10)}.md`.replace(/[^a-zA-Z0-9._-]+/g, "_");
        downloadBlob(synergyToMarkdown(result), name, "text/markdown;charset=utf-8");
    };

    if (firms === null) return <div className="p-12"><Loader label="Loading firms" /></div>;

    if (firms.length < 2) {
        return (
            <div className="p-8" data-testid="synergy-page">
                <header className="mb-8 border-b border-zinc-800 pb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Synergy</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Cross-firm product fit</h1>
                </header>
                <div className="border border-dashed border-zinc-800 p-16 text-center">
                    <GitCompareArrows className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-heading font-bold text-zinc-300 text-lg mb-2">Need at least 2 firms</div>
                    <div className="text-sm text-zinc-500 mb-5">Research and save two firms before running a synergy analysis.</div>
                    <Link to="/app/research" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5">
                        Research a firm <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8" data-testid="synergy-page">
            <header className="mb-8 border-b border-zinc-800 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Synergy</div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Cross-firm product fit</h1>
                <p className="text-sm text-zinc-500 mt-2">Pick a product from one firm. Pick another firm. Claude tells you why it fits — and what it'd earn.</p>
            </header>

            <div className="border border-zinc-800 bg-zinc-950 p-5 mb-6">
                <div className="flex items-center justify-end mb-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="auto-best-toggle-wrap">
                        <input
                            type="checkbox"
                            checked={autoBest}
                            onChange={(e) => setAutoBest(e.target.checked)}
                            data-testid="auto-best-toggle"
                            className="accent-amber-500"
                        />
                        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                            <Wand2 className="w-3 h-3 text-amber-500" /> Auto-pick best product
                        </span>
                    </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FirmSelect label="Source firm (A)" value={firmAId} onChange={setFirmAId} firms={firms} excludeId={firmBId} testid="synergy-firm-a-select" />
                    {autoBest ? (
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Product</label>
                            <div className="w-full bg-zinc-950 border border-amber-500/40 px-3 py-3 text-sm text-amber-400 font-mono inline-flex items-center gap-2">
                                <Wand2 className="w-3.5 h-3.5" /> AI will pick the best one
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Product from A</label>
                            <select
                                data-testid="synergy-product-select"
                                value={productId || ""}
                                onChange={(e) => setProductId(e.target.value || null)}
                                disabled={!firmA || !products.length}
                                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500 font-mono disabled:opacity-40"
                            >
                                {!firmA && <option value="">— pick firm A first —</option>}
                                {firmA && !products.length && <option value="">— no products on this firm —</option>}
                                {products.map((p) => (
                                    <option key={p.id} value={p.id}>{`${p.name} · ${fmtMoney(p.annual_revenue_usd)}`}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <FirmSelect label="Target firm (B)" value={firmBId} onChange={setFirmBId} firms={firms} excludeId={firmAId} testid="synergy-firm-b-select" />
                </div>
                <div className="flex justify-end mt-5">
                    <button
                        onClick={run}
                        disabled={!firmAId || !firmBId || (!autoBest && !productId) || loading}
                        data-testid="run-synergy-btn"
                        className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-5 py-3 flex items-center gap-2"
                    >
                        <Sparkles className="w-4 h-4" /> {autoBest ? "Find best fit" : "Analyze fit"}
                    </button>
                </div>
            </div>

            {error && (
                <div data-testid="synergy-error" className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6">{error}</div>
            )}

            {reopenInfo && !result && !loading && (
                <div data-testid="synergy-reopen-info" className="border border-amber-500/40 bg-amber-500/5 text-amber-300 text-sm px-4 py-3 font-mono mb-6">
                    {reopenInfo}
                </div>
            )}

            {loading && (
                <div className="border border-zinc-800 bg-zinc-950 p-12">
                    <Loader label="Computing strategic fit" stages={LoaderStages.synergy} />
                </div>
            )}

            {result && !loading && (
                <div className="animate-fade-in space-y-4" data-testid="synergy-result">
                    {/* Header strip */}
                    <div className="border border-zinc-800 bg-zinc-950 p-6">
                        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">// Verdict · Claude Sonnet 4.5</div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={pinSynergy}
                                    disabled={pinning || pinned}
                                    data-testid="pin-synergy-btn"
                                    title={result.fit_score < 70 ? "Pin this synergy so it shows up in the Library despite being below 70" : "Pin this synergy"}
                                    className={`inline-flex items-center gap-1.5 border text-xs font-mono uppercase tracking-wider px-2.5 py-2 transition-colors ${
                                        pinned
                                            ? "border-amber-500/60 bg-amber-500/10 text-amber-400"
                                            : "border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-400"
                                    } disabled:opacity-60`}
                                >
                                    <Pin className="w-3 h-3" /> {pinned ? "Pinned" : pinning ? "Pinning…" : "Pin synergy"}
                                </button>
                                <button
                                    onClick={() => draftEmail()}
                                    disabled={draftingEmail}
                                    data-testid="draft-email-btn"
                                    title="Generate a BD intro email to send to FIRM_B"
                                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-50"
                                >
                                    <Mail className="w-3 h-3" /> {draftingEmail ? "Drafting…" : "Intro email"}
                                </button>
                                <button
                                    onClick={() => draftSequence()}
                                    disabled={draftingSeq}
                                    data-testid="draft-sequence-btn"
                                    title="Generate a 5-touch BD email cadence over 14 days"
                                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-50"
                                >
                                    <Mail className="w-3 h-3" /> {draftingSeq ? "Drafting…" : "5-email sequence"}
                                </button>
                                <button
                                    onClick={generateShareLink}
                                    disabled={sharing}
                                    data-testid="share-link-btn"
                                    title="Mint a public pitch link you can paste into LinkedIn / outbound emails"
                                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-50"
                                >
                                    <Link2 className="w-3 h-3" /> {sharing ? "Minting…" : (shareUrl ? (shareCopied ? "Copied!" : "Copy link") : "Share link")}
                                </button>
                                {result && result.firm_a?.id && result.firm_b?.id && result.product?.id && (
                                    <a
                                        href={`/app/pitch?key=${encodeURIComponent(`${result.firm_a.id}:${result.firm_b.id}:${result.product.id}`)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="pitch-deck-btn"
                                        title="Open print-friendly pitch deck (Save as PDF from the browser dialog)"
                                        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors"
                                    >
                                        <FileText className="w-3 h-3" /> Pitch deck
                                    </a>
                                )}
                                <button
                                    onClick={downloadMd}
                                    data-testid="download-synergy-md"
                                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors"
                                >
                                    <Download className="w-3 h-3" /> Markdown
                                </button>
                            </div>
                        </div>

                        {/* A — Public share-link banner */}
                        {(shareUrl || shareError) && (
                            <div className="mb-4" data-testid="share-link-banner-wrap">
                                <div className="border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="share-link-banner">
                                    {shareError ? (
                                        <div className="text-xs text-red-400 font-mono">{shareError}</div>
                                    ) : (
                                        <>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1">Public pitch URL · paste in email / LinkedIn</div>
                                                <a href={shareUrl} target="_blank" rel="noreferrer" className="font-mono text-xs text-emerald-300 hover:text-emerald-200 break-all" data-testid="share-link-url">{shareUrl}</a>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button onClick={generateShareLink} data-testid="share-link-copy" className="text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:border-emerald-500 hover:text-emerald-400 text-zinc-300 px-2 py-1 transition-colors">
                                                    <Copy className="w-3 h-3 inline" /> {shareCopied ? "Copied!" : "Copy"}
                                                </button>
                                                <button onClick={revokeShareLink} data-testid="share-link-revoke" className="text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-500 px-2 py-1 transition-colors">
                                                    Revoke
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {/* Engagement analytics — only renders when we have a token */}
                                {shareUrl && !shareError && (
                                    <div className="mt-2">
                                        <ShareAnalytics token={extractToken(shareUrl)} kind="pitch" />
                                    </div>
                                )}
                                {/* Per-pitch trackable reply alias + manual replied toggle */}
                                {shareUrl && !shareError && replyEmail && (
                                    <div className="mt-2 border border-emerald-500/30 bg-emerald-500/[0.04] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="reply-tracker">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1 flex items-center gap-1.5">
                                                <Mail className="w-3 h-3" /> Trackable reply alias
                                            </div>
                                            <button
                                                type="button"
                                                onClick={copyReplyAlias}
                                                data-testid="reply-alias-copy"
                                                className="font-mono text-xs text-emerald-300 hover:text-emerald-200 break-all text-left"
                                                title="Click to copy"
                                            >
                                                {replyEmail}
                                                {replyAliasCopied && <span className="ml-2 text-emerald-500">· copied!</span>}
                                            </button>
                                            <div className="font-mono text-[9px] text-zinc-600 mt-1">Encodes the pitch token — when wired, inbound replies auto-thread to this synergy.</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={toggleReplied}
                                            disabled={replyToggling}
                                            data-testid={replied ? "reply-unmark-btn" : "reply-mark-btn"}
                                            className={`shrink-0 text-[10px] font-mono uppercase tracking-wider border px-3 py-1.5 transition-colors ${
                                                replied
                                                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/20"
                                                    : "border-zinc-700 text-zinc-300 hover:border-emerald-500 hover:text-emerald-400"
                                            }`}
                                        >
                                            {replyToggling ? "…" : replied ? "✓ Replied · undo" : "Mark as replied"}
                                        </button>
                                    </div>
                                )}
                                {/* Stale-pitch nudge — manual on-demand re-engagement draft. */}
                                {shareUrl && !shareError && !replied && (
                                    <div className="mt-2 border border-amber-500/30 bg-amber-500/[0.04] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="nudge-control">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400 mb-1">// Stale-pitch nudge</div>
                                            <div className="text-xs text-zinc-300">
                                                Auto-fires 48h after the first prospect view. {lastNudgeAt
                                                    ? <>Last drafted <span className="text-amber-400">{new Date(lastNudgeAt).toLocaleDateString()}</span>.</>
                                                    : <>Or trigger a fresh draft now.</>}
                                            </div>
                                            {nudgeMsg && <div className="font-mono text-[10px] text-emerald-400 mt-1" data-testid="nudge-msg">{nudgeMsg}</div>}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={draftNudge}
                                            disabled={nudging}
                                            data-testid="nudge-draft-btn"
                                            className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider border border-amber-500 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 hover:text-amber-200 px-3 py-1.5 transition-colors disabled:opacity-50"
                                        >
                                            {nudging ? "Drafting…" : (lastNudgeAt ? "Re-draft nudge" : "Draft a nudge")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
                            <div className="min-w-0">
                                <div className="flex items-center gap-3 flex-wrap font-heading font-black text-2xl text-zinc-50">
                                    {firmColors[result.firm_a.id] && <span className="w-3 h-3 rounded-full inline-block" style={{ background: firmColors[result.firm_a.id] }} data-testid="firm-a-color-dot" />}
                                    <span>{result.firm_a.name}</span>
                                    <span className="text-zinc-700">·</span>
                                    <span className="text-amber-500">{result.product.name}</span>
                                    <ArrowRight className="w-5 h-5 text-zinc-600" />
                                    {firmColors[result.firm_b.id] && <span className="w-3 h-3 rounded-full inline-block" style={{ background: firmColors[result.firm_b.id] }} data-testid="firm-b-color-dot" />}
                                    <span>{result.firm_b.name}</span>
                                </div>
                                <div className="font-mono text-xs text-zinc-500 mt-1">
                                    {result.firm_a.industry || "—"} → {result.firm_b.industry || "—"}
                                </div>
                            </div>
                            <FitGauge score={result.fit_score} />
                        </div>
                        <p className="text-sm text-zinc-300 leading-relaxed">{result.rationale}</p>

                        {/* Mutual fit — reverse direction */}
                        {result.reverse_fit_score > 0 && (
                            <div className="mt-4 border-t border-zinc-800 pt-4" data-testid="mutual-fit-block">
                                <div className="flex items-center gap-3 flex-wrap mb-2">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Mutual fit</div>
                                    <span className="font-mono text-[10px] text-zinc-600">
                                        {result.firm_b.name} → {result.firm_a.name} interest:
                                    </span>
                                    <span
                                        data-testid="reverse-fit-score"
                                        className={`inline-block border px-2 py-0.5 font-mono text-xs ${
                                            result.reverse_fit_score >= 90 ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" :
                                            result.reverse_fit_score >= 80 ? "text-blue-400 border-blue-500/40 bg-blue-500/10" :
                                            result.reverse_fit_score >= 70 ? "text-amber-400 border-amber-500/40 bg-amber-500/10" :
                                            "text-zinc-400 border-zinc-700"
                                        }`}
                                    >
                                        {result.reverse_fit_score}/100
                                    </span>
                                    {result.reverse_fit_score >= 70 && result.fit_score >= 70 && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-400 uppercase tracking-wider"><RefreshCw className="w-3 h-3" /> Bilateral fit</span>
                                    )}
                                </div>
                                {result.reverse_rationale && (
                                    <p className="text-xs text-zinc-400 leading-relaxed">{result.reverse_rationale}</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Selling points table — why FIRM_B should be interested in FIRM_A's product */}
                    {result.selling_points?.length > 0 && (
                        <div className="border border-zinc-800 bg-zinc-950" data-testid="selling-points-table-wrap">
                            <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-amber-500" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                                    Why <span className="text-zinc-50">{result.firm_b.name}</span> should want <span className="text-amber-400">{result.product.name}</span>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="selling-points-table">
                                    <thead>
                                        <tr className="border-b border-zinc-800/60">
                                            <th className="text-left px-5 py-2.5 bg-zinc-900/50 font-mono text-[10px] uppercase tracking-wider text-zinc-500 w-12">#</th>
                                            <th className="text-left px-3 py-2.5 bg-zinc-900/50 font-mono text-[10px] uppercase tracking-wider text-zinc-500 w-1/3">Point</th>
                                            <th className="text-left px-5 py-2.5 bg-zinc-900/50 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Why it matters</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.selling_points.map((sp, i) => (
                                            <tr key={i} className="border-b border-zinc-800/60 last:border-b-0" data-testid={`selling-point-${i}`}>
                                                <td className="px-5 py-3 font-mono text-xs text-amber-500">{String(i + 1).padStart(2, "0")}</td>
                                                <td className="px-3 py-3 font-heading font-bold text-zinc-50 text-sm">{sp.point}</td>
                                                <td className="px-5 py-3 text-sm text-zinc-300 leading-relaxed">{sp.detail}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Pros / Cons table */}
                    {(result.pros_cons?.pros?.length > 0 || result.pros_cons?.cons?.length > 0) && (
                        <div className="border border-zinc-800 bg-zinc-950" data-testid="pros-cons-table-wrap">
                            <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                                <GitCompareArrows className="w-4 h-4 text-amber-500" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Pluses &amp; minuses</div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="pros-cons-table">
                                    <thead>
                                        <tr className="border-b border-zinc-800/60">
                                            <th className="text-left px-5 py-2.5 bg-zinc-900/50 font-mono text-[10px] uppercase tracking-wider text-emerald-400 w-1/2">
                                                <span className="inline-flex items-center gap-1.5"><Plus className="w-3 h-3" /> Pluses</span>
                                            </th>
                                            <th className="text-left px-5 py-2.5 bg-zinc-900/50 font-mono text-[10px] uppercase tracking-wider text-red-400 w-1/2 border-l border-zinc-800">
                                                <span className="inline-flex items-center gap-1.5"><Minus className="w-3 h-3" /> Minuses</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: Math.max(result.pros_cons.pros?.length || 0, result.pros_cons.cons?.length || 0) }).map((_, i) => (
                                            <tr key={i} className="border-b border-zinc-800/60 last:border-b-0 align-top">
                                                <td className="px-5 py-3 text-sm text-zinc-200 leading-relaxed" data-testid={`pro-${i}`}>
                                                    {result.pros_cons.pros?.[i] ? (
                                                        <span className="inline-flex items-start gap-2"><Plus className="w-3 h-3 text-emerald-400 mt-1 shrink-0" />{result.pros_cons.pros[i]}</span>
                                                    ) : <span className="text-zinc-700">—</span>}
                                                </td>
                                                <td className="px-5 py-3 text-sm text-zinc-200 leading-relaxed border-l border-zinc-800" data-testid={`con-${i}`}>
                                                    {result.pros_cons.cons?.[i] ? (
                                                        <span className="inline-flex items-start gap-2"><Minus className="w-3 h-3 text-red-400 mt-1 shrink-0" />{result.pros_cons.cons[i]}</span>
                                                    ) : <span className="text-zinc-700">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* All scores table — shown when AI compared all products */}
                    {result.all_scores?.length > 0 && (
                        <div className="border border-zinc-800 bg-zinc-950 p-5" data-testid="all-scores-table">
                            <div className="flex items-center gap-2 mb-3">
                                <Wand2 className="w-4 h-4 text-amber-500" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">All products scored · best pick highlighted</div>
                            </div>
                            <div className="space-y-1">
                                {result.all_scores.map((s) => {
                                    const isPicked = s.product_id === result.product.id;
                                    const tone = s.fit_score >= 90 ? "text-emerald-400" : s.fit_score >= 80 ? "text-blue-400" : s.fit_score >= 70 ? "text-yellow-400" : "text-zinc-500";
                                    return (
                                        <div key={s.product_id} className={`flex items-center justify-between px-3 py-2 ${isPicked ? "bg-amber-500/10 border-l-2 border-amber-500" : "border-l-2 border-transparent hover:bg-zinc-900/40"}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                {isPicked && <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />}
                                                <span className={`text-sm truncate ${isPicked ? "text-amber-300 font-semibold" : "text-zinc-300"}`}>{s.product_name}</span>
                                            </div>
                                            <span className={`font-mono text-sm ${tone}`}>{s.fit_score}/100</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Product image (AI generated, lazy on click) */}
                    <div className="border border-zinc-800 bg-zinc-950 p-5" data-testid="synergy-product-image-card">
                        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                                <ImageIcon className="w-4 h-4 text-amber-500" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Best-fit product · visualization</div>
                            </div>
                            {!productImage && (
                                <button
                                    onClick={generateProductImage}
                                    disabled={genImg}
                                    data-testid="generate-image-btn"
                                    className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-2 transition-colors disabled:opacity-60"
                                    title="Generate an AI image of this product (~1 credit, cached after first call)"
                                >
                                    <Sparkles className="w-3 h-3" /> {genImg ? "Generating…" : "Generate image"}
                                </button>
                            )}
                        </div>
                        {imgError && <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-xs px-3 py-2 font-mono mb-3" data-testid="image-error">{imgError}</div>}
                        {genImg && !productImage && (
                            <div className="flex items-center justify-center h-64 border border-zinc-800 bg-black/40">
                                <Loader label="Imagining the product" stages={LoaderStages.image} />
                            </div>
                        )}
                        {productImage && (
                            <div className="border border-zinc-800 bg-black overflow-hidden">
                                <img
                                    src={`data:image/${productImage.startsWith("iVBORw0KGgo") ? "png" : "jpeg"};base64,${productImage}`}
                                    alt={result.product.name}
                                    data-testid="product-image"
                                    className="block w-full h-auto max-h-[480px] object-cover"
                                />
                                <div className="px-3 py-2 border-t border-zinc-800 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    {result.product.name} — generated by Gemini Nano Banana
                                </div>
                            </div>
                        )}
                        {!productImage && !genImg && !imgError && (
                            <div className="border border-dashed border-zinc-800 p-6 text-center text-xs font-mono text-zinc-600">Click "Generate image" to visualize the best-fit product</div>
                        )}
                    </div>

                    {/* Key metrics — compact table */}
                    <div className="border border-zinc-800 bg-zinc-950" data-testid="synergy-metrics-table-wrap">
                        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Key metrics</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm font-mono" data-testid="synergy-metrics-table">
                                <tbody>
                                    <tr className="border-b border-zinc-800/60">
                                        <th className="text-left px-5 py-3 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500 w-1/3">Fit score</th>
                                        <td className="px-5 py-3 text-amber-500">{result.fit_score}/100</td>
                                    </tr>
                                    <tr className="border-b border-zinc-800/60">
                                        <th className="text-left px-5 py-3 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Projected revenue at {result.firm_b.name}</th>
                                        <td className="px-5 py-3 text-amber-500">{fmtMoney(result.estimated_added_annual_revenue_usd)}</td>
                                    </tr>
                                    <tr className="border-b border-zinc-800/60">
                                        <th className="text-left px-5 py-3 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Share of {result.firm_b.name}'s revenue</th>
                                        <td className="px-5 py-3 text-zinc-200">{result.estimated_revenue_share_pct.toFixed(1)}%</td>
                                    </tr>
                                    <tr className="border-b border-zinc-800/60">
                                        <th className="text-left px-5 py-3 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Reference revenue at {result.firm_a.name}</th>
                                        <td className="px-5 py-3 text-zinc-300">{fmtMoney(result.product.annual_revenue_usd)} <span className="text-zinc-600">·</span> {Number(result.product.growth_pct).toFixed(1)}% YoY</td>
                                    </tr>
                                    <tr>
                                        <th className="text-left px-5 py-3 bg-zinc-900/50 text-[10px] uppercase tracking-wider text-zinc-500">Existing relationship</th>
                                        <td className="px-5 py-3">
                                            <span className={result.existing_relationship ? "text-emerald-400" : "text-zinc-400"}>
                                                {result.existing_relationship ? "ACTIVE" : "NONE KNOWN"}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Existing relationship */}
                    <div
                        data-testid="existing-relationship"
                        className={`border p-5 ${result.existing_relationship ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-800 bg-zinc-950"}`}
                    >
                        <div className="flex items-start gap-3">
                            {result.existing_relationship
                                ? <Link2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                                : <Unlink className="w-5 h-5 text-zinc-500 shrink-0 mt-0.5" />}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Current relationship</div>
                                    <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${result.existing_relationship ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-700 bg-zinc-900 text-zinc-400"}`}>
                                        {result.existing_relationship ? "ACTIVE" : "NONE KNOWN"}
                                    </span>
                                </div>
                                <p className="text-sm text-zinc-200 leading-relaxed">{result.relationship_summary || "—"}</p>
                                {result.existing_products_from_a_used_by_b?.length > 0 && (
                                    <div className="mt-3">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">
                                            Products {result.firm_b.name} already uses from {result.firm_a.name}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5" data-testid="existing-products-list">
                                            {result.existing_products_from_a_used_by_b.map((p, i) => (
                                                <span key={i} className="text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5">{p}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Revenue */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="border border-zinc-800 bg-zinc-950 p-5">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">Projected revenue from THIS product only</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">at {result.firm_b.name}</div>
                            <div className="font-heading font-black text-4xl text-amber-500" data-testid="projected-revenue">{fmtMoney(result.estimated_added_annual_revenue_usd)}</div>
                            <div className="font-mono text-xs text-zinc-500 mt-2">≈ {result.estimated_revenue_share_pct.toFixed(1)}% of {result.firm_b.name}'s total revenue</div>
                            <div className="mt-4 pt-4 border-t border-zinc-800">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">For reference · same product at {result.firm_a.name}</div>
                                <div className="font-mono text-sm text-zinc-300">{fmtMoney(result.product.annual_revenue_usd)} <span className="text-zinc-600">·</span> {Number(result.product.growth_pct).toFixed(1)}% YoY</div>
                            </div>
                        </div>
                        <div className="lg:col-span-2 border border-zinc-800 bg-zinc-950 p-5">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">12-month ramp · THIS product only</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">monthly revenue at {result.firm_b.name}</div>
                            <div style={{ width: "100%", height: 220, minWidth: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={result.monthly_revenue_projection || []}>
                                        <XAxis dataKey="month" stroke="#52525B" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#fafafa" }} />
                                        <YAxis stroke="#52525B" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#fafafa" }} tickFormatter={fmtMoney} width={60} />
                                        <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11, color: "#fafafa" }} formatter={(v) => fmtMoney(v)} />
                                        <Line type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={2} dot={{ fill: "#F59E0B", r: 2 }} activeDot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>

                    {/* Use cases / Integration / Risks */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <Panel title="Use cases" icon={Lightbulb} items={result.use_cases} testid="use-cases" />
                        <Panel title="Integration notes" icon={TrendingUp} items={result.integration_notes} testid="integration-notes" />
                        <Panel title="Risks" icon={AlertTriangle} items={result.risks} tone="red" testid="risks" />
                    </div>

                    {/* Full product card */}
                    <div className="border border-zinc-800 bg-zinc-950 p-5">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Full product · {result.product.name}</div>
                        <p className="text-sm text-zinc-300 leading-relaxed mb-4">{result.product.description || "—"}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { l: "Pricing", v: result.product.pricing || "—" },
                                { l: "Annual revenue (A)", v: fmtMoney(result.product.annual_revenue_usd) },
                                { l: "Units sold (A)", v: (result.product.units_sold || 0).toLocaleString() },
                                { l: "YoY growth (A)", v: `${Number(result.product.growth_pct).toFixed(1)}%` },
                            ].map((s) => (
                                <div key={s.l} className="border border-zinc-800 p-3 bg-black/40">
                                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{s.l}</div>
                                    <div className="font-heading font-bold text-zinc-50">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* C — Email sequence modal */}
            {(sequence || draftingSeq || seqError) && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="sequence-modal">
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between border-b border-zinc-800 p-4">
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-amber-500" />
                                <div className="font-heading font-bold text-zinc-50">5-email cadence · {result?.firm_a?.name} → {result?.firm_b?.name}</div>
                            </div>
                            <button
                                onClick={() => { setSequence(null); setSeqError(""); }}
                                data-testid="close-sequence-modal"
                                className="text-zinc-500 hover:text-zinc-100"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="px-4 pt-4 flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Tone:</span>
                            {["casual", "formal"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => { setEmailTone(t); draftSequence(t); }}
                                    disabled={draftingSeq}
                                    data-testid={`seq-tone-${t}`}
                                    className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors disabled:opacity-50 ${
                                        emailTone === t ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                            {sequence && (
                                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    {sequence.emails?.length || 0} emails · {sequence.emails?.[sequence.emails.length - 1]?.day_offset || 0}-day cadence
                                </span>
                            )}
                        </div>
                        <div className="p-4 overflow-y-auto flex-1 space-y-4">
                            {draftingSeq && <div className="p-4"><Loader label="Drafting 5-email cadence" stages={LoaderStages.emailSequence} /></div>}
                            {seqError && (
                                <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-3 py-2 font-mono" data-testid="sequence-error-msg">{seqError}</div>
                            )}
                            {sequence && sequence.emails?.map((em, i) => (
                                <article key={i} className="border border-zinc-800 bg-black/40 p-4" data-testid={`sequence-email-${i}`}>
                                    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[10px] uppercase tracking-wider border border-amber-500/40 bg-amber-500/10 text-amber-400 px-2 py-0.5">
                                                Day {em.day_offset}
                                            </span>
                                            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{em.type}</span>
                                        </div>
                                        <button
                                            onClick={() => copySequenceEmail(i)}
                                            data-testid={`copy-sequence-${i}`}
                                            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-[10px] font-mono uppercase tracking-wider text-zinc-400 px-2 py-1 transition-colors"
                                        >
                                            <Copy className="w-3 h-3" /> {seqCopiedIdx === i ? "Copied!" : "Copy"}
                                        </button>
                                    </div>
                                    <div className="mb-2">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-0.5">Subject</div>
                                        <div className="font-heading font-bold text-zinc-50 text-sm">{em.subject}</div>
                                    </div>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-0.5">Body</div>
                                        <pre className="text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed font-sans">{em.body}</pre>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Outreach email modal */}
            {(email || draftingEmail || emailError) && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="email-modal">
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between border-b border-zinc-800 p-4">
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-amber-500" />
                                <div className="font-heading font-bold text-zinc-50">Intro email · {result?.firm_a?.name} → {result?.firm_b?.name}</div>
                            </div>
                            <button
                                onClick={() => { setEmail(null); setEmailError(""); }}
                                data-testid="close-email-modal"
                                className="text-zinc-500 hover:text-zinc-100"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="px-4 pt-4 flex items-center gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Tone:</span>
                            {["casual", "formal"].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => { setEmailTone(t); draftEmail(t); }}
                                    disabled={draftingEmail}
                                    data-testid={`tone-${t}`}
                                    className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors disabled:opacity-50 ${
                                        emailTone === t ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                            <div className="ml-auto">
                                {email && (
                                    <button
                                        onClick={copyEmailToClipboard}
                                        data-testid="copy-email-btn"
                                        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2.5 py-1.5 transition-colors"
                                    >
                                        <Copy className="w-3 h-3" /> {emailCopied ? "Copied!" : "Copy"}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="p-4 overflow-y-auto flex-1">
                            {draftingEmail && <div className="p-4"><Loader label="Drafting email" stages={LoaderStages.email} /></div>}
                            {emailError && (
                                <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-3 py-2 font-mono" data-testid="email-error-msg">{emailError}</div>
                            )}
                            {email && !draftingEmail && (
                                <div data-testid="email-content">
                                    <div className="mb-3">
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Subject</div>
                                        <div className="font-heading font-bold text-zinc-50 text-base" data-testid="email-subject">{email.subject}</div>
                                    </div>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Body</div>
                                        <pre className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed font-sans" data-testid="email-body">{email.body}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Panel({ title, icon: Icon, items, tone, testid }) {
    const accent = tone === "red" ? "text-red-400" : "text-amber-500";
    return (
        <div className="border border-zinc-800 bg-zinc-950 p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className={`w-4 h-4 ${accent}`} />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{title}</div>
            </div>
            <ul className="space-y-2">
                {(items || []).map((x, i) => (
                    <li key={i} className="text-sm text-zinc-300 border-l-2 border-zinc-800 pl-3 leading-relaxed">{x}</li>
                ))}
                {!items?.length && <li className="text-xs text-zinc-600">—</li>}
            </ul>
        </div>
    );
}
